import React from "react";
import { FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert } from "react-bootstrap";

interface FormErrorSummaryProps {
  errors: FieldErrors;
}

/**
 * Recursively extracts all error messages from a react-hook-form FieldErrors object.
 * Handles nested objects (e.g. contact.city, socialMedia.facebook).
 */
const extractErrors = (errors: FieldErrors, prefix = ""): string[] => {
  const messages: string[] = [];

  for (const key of Object.keys(errors)) {
    const error = errors[key];
    if (!error) continue;

    const fieldPath = prefix ? `${prefix}.${key}` : key;

    if (error.message && typeof error.message === "string") {
      messages.push(error.message);
    } else if (typeof error === "object" && !error.message) {
      // Nested object (e.g. contact: { city: { message: "..." } })
      messages.push(...extractErrors(error as FieldErrors, fieldPath));
    }
  }

  return messages;
};

/**
 * Displays a Bootstrap alert at the top of the form listing all validation errors.
 * This ensures errors are always visible even when the invalid field is scrolled out of view.
 */
const FormErrorSummary: React.FC<FormErrorSummaryProps> = ({ errors }) => {
  const { t } = useTranslation();
  const errorMessages = extractErrors(errors);

  if (errorMessages.length === 0) return null;

  return (
    <Alert variant="danger" className="mb-3">
      <Alert.Heading className="h6 mb-2">
        <i className="ti ti-alert-circle me-2"></i>
        {t("formErrors.summaryTitle")}
      </Alert.Heading>
      <ul className="mb-0 ps-3">
        {errorMessages.map((msg, index) => (
          <li key={index}>{t(msg)}</li>
        ))}
      </ul>
    </Alert>
  );
};

export default FormErrorSummary;
